import { UnifiedMonitoringAlarmTypeQueue } from '@hakimo-ui/hakimo/types';
import { Button } from '@hakimo-ui/shared/ui-base';
import { ClockIcon } from '@heroicons/react/24/outline';
import {
  BellAlertIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/solid';

interface EmergencyAlarmsProps {
  queues: UnifiedMonitoringAlarmTypeQueue[];
  fetchAlarmTypeAlarm: (alarmTypeId: string) => void;
  isLoading: boolean;
}

export function EmergencyAlarms(props: EmergencyAlarmsProps) {
  const { queues, fetchAlarmTypeAlarm, isLoading } = props;
  const pendingQueues = queues.filter((queue) => queue.count > 0);
  if (pendingQueues.length === 0) {
    return null;
  }
  return (
    <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 w-96 shrink-0 rounded-lg p-4">
      <div className="text-onlight-text-1 dark:text-ondark-text-1 mb-4 flex items-center gap-2 text-lg font-semibold">
        <ExclamationCircleIcon className="h-6 w-6 text-red-500" />
        Emergency Alarms
      </div>
      <ul className="space-y-3">
        {pendingQueues.map((queue) => (
          <li
            key={queue.id}
            className="bg-onlight-bg-2 dark:bg-ondark-bg-2 flex items-center gap-3 rounded-lg p-3"
          >
            <div className="bg-onlight-bg-3 dark:bg-ondark-bg-3 flex h-12 w-12 shrink-0 items-center justify-center rounded-full">
              <BellAlertIcon className="h-6 w-6 text-red-500" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-onlight-text-1 dark:text-ondark-text-1 truncate text-sm font-medium">
                {queue.name}
              </div>
              <div className="text-onlight-text-2 dark:text-ondark-text-2 flex items-baseline gap-2 text-xs">
                <span className="text-3xl tabular-nums text-red-500">
                  {queue.count}
                </span>
                pending
                {queue.oldest_enqueued_at !== null && (
                  <span className="flex items-center gap-1">
                    <ClockIcon className="h-3.5 w-3.5" />
                    {timeAgo(queue.oldest_enqueued_at)}
                  </span>
                )}
              </div>
            </div>
            <Button
              variant="primary"
              onClick={() => fetchAlarmTypeAlarm(queue.id)}
              disabled={isLoading}
              className="px-3 py-1 text-xs"
            >
              Get Alarm
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// "12 min ago" under an hour, "2 h 5 m ago" after.
export const timeAgo = (epochSeconds: number) => {
  const minutes = Math.max(
    0,
    Math.floor((Date.now() / 1000 - epochSeconds) / 60)
  );
  return minutes < 60
    ? `${minutes} min ago`
    : `${Math.floor(minutes / 60)} h ${minutes % 60} m ago`;
};
